import { eventNamespaceFactory } from '@opten/events/event-namespace-factory';
import { removeClass } from '@opten/dom/remove-class';
import { addClass } from '@opten/dom/add-class';
import { hasClass } from '@opten/dom/has-class';
import { hasAnyParentsWithClass } from '@opten/dom/has-any-parents-with-class';
import { LoadingIndicator } from '../loading-indicator/loading-indicator';
import { isDesktop } from '../../utilities/detection';
import { Collapse } from '@opten/collapse/initializer';

export class TabNavigationElement {
    index: number;
    navigation: HTMLElement;
    content: TabContentElement;
    target: string;
}

export class TabContentElement {
    element: HTMLElement;
    loaded: boolean;
}

export class TabsOptions {
    selectors: {
        navigation: string;
        content: string;
    };
    loading: {
        contentLoading: string;
    };
    classes: {
        active: string;
        visible: string;
    };
    attributes: {
        contentIdentifier: string;
        navigationToContent: string;
        target: string;
    }
}

export class Tabs {
    private _wrapperElement: HTMLElement;
    private _tabElements: TabNavigationElement[] = [];
    private _visibleTabIndex: number = null;
    private _options: TabsOptions = {
        selectors: {
            navigation: 'js-tab-link',
            content: 'js-tab-content'
        },
        classes: {
            active: 'is-active',
            visible: 'is-visible'
        },
        loading: {
            contentLoading: ''
        },
        attributes: {
            contentIdentifier: 'data-tab',
            navigationToContent: 'data-tab-action',
            target: 'data-target'
        }
    }
    // event names
    private eventNamespaceFn = eventNamespaceFactory('merbag.tabs');


    constructor(private wrapper: HTMLElement, options?: TabsOptions) {
        this._wrapperElement = wrapper;
        this._options = { ...this._options, ...options };
        this._options.loading.contentLoading = new LoadingIndicator().contentLoading;
        this.init();
    }


    private init() {
        this.initTabElements();
        this.initTabEvents();
        if (hasClass(document.body, 'js-tabs-site')) {
            this.initTabsSite();
        }
    }

    private initTabElements() {
        const contentElements = Array.from(this._wrapperElement.getElementsByClassName(this._options.selectors.content));
        if (contentElements != null && contentElements.length > 0) {
            const navigationElements = Array.from(this._wrapperElement.getElementsByClassName(this._options.selectors.navigation));
            let i = 0;
            if (navigationElements != null && navigationElements.length > 0) {
                navigationElements.forEach(navigationElement => {
                    const tabTarget = navigationElement.getAttribute(this._options.attributes.navigationToContent);
                    if (tabTarget != null) {
                        const filteredContents = contentElements.filter(contentElement => {
                            const contentIdentifier = contentElement.getAttribute(this._options.attributes.contentIdentifier)
                            return contentIdentifier != null && contentIdentifier === tabTarget;
                        });
                        if (filteredContents != null && filteredContents.length > 0) {
                            const tabContent = filteredContents[0] as HTMLElement;
                            if (tabContent != null) {
                                const target = navigationElement.getAttribute(this._options.attributes.target);
                                const hasTarget = target != null && target.length > 0;
                                const contentElement: TabContentElement = {
                                    element: tabContent,
                                    loaded: hasTarget === false
                                };
                                const tabElement: TabNavigationElement = {
                                    index: i++,
                                    navigation: navigationElement as HTMLElement,
                                    content: contentElement,
                                    target: hasTarget ? target : ''
                                };
                                this._tabElements.push(tabElement);
                            }
                        }
                    }
                });
            }
        };
    }

    private initTabEvents() {
        if (this._tabElements.length > 0) {
            this._tabElements.forEach(tabElement => {
                tabElement.navigation.addEventListener('click', (e) => this.handleClickTab(tabElement, e));
            });
            const activeTabElements = this._tabElements.filter(tabElement => hasClass(tabElement.navigation, this._options.classes.active));
            if (activeTabElements != null && activeTabElements.length > 0) {
                this.handleClickTab(activeTabElements[0]);
            }
        }
    }

    private initTabsSite() {
        const isInsideSidePanel = hasAnyParentsWithClass(this.wrapper, ['js-side-panel']);
        let contentElementsSite = Array.from(document.body.getElementsByClassName(this._options.selectors.content));
        contentElementsSite = contentElementsSite && contentElementsSite.length > 0 ? contentElementsSite.filter(o => isInsideSidePanel ? !hasAnyParentsWithClass(o, ['js-side-panel']) : hasAnyParentsWithClass(o, ['js-side-panel'])) : null;
        if (contentElementsSite && contentElementsSite.length > 0) {
            contentElementsSite.forEach(contentElement => {
                contentElement.addEventListener(
                    'shown.merbag.tabs', () => this.handleClickTabsSiteContentElement(contentElement, !isInsideSidePanel)
                );
            });
        }
    }

    private handleClickTabsSiteContentElement(contentElement: Element, isInsideSidePanel: boolean) {
        const tabTarget = contentElement.getAttribute('data-tab');
        const triggeredTab = tabTarget && tabTarget.length > 0 ? this._tabElements.filter(o => o.content.element.getAttribute('data-tab') === tabTarget) : null;
        if (triggeredTab && triggeredTab.length > 0) {
            this.handleClickTab(triggeredTab[0]);
            if (isInsideSidePanel === false && isDesktop() === false) {
                const api = Collapse.getInstance(contentElement as HTMLElement);
                if (api && api.isOpen() === false) {
                    api.show();
                }
            }
        }
    }

    private handleClickTab(tab: TabNavigationElement, event: Event = null): void {
        if (event != null) {
            event.stopPropagation();
            event.preventDefault();
        }
        const hasVisibleTab = this._visibleTabIndex !== null;
        if ((this._visibleTabIndex !== null && this._visibleTabIndex === tab.index) === false) {
            if (hasVisibleTab) {
                this.handleHideTab(this._tabElements.filter(tabElement => tabElement.index === this._visibleTabIndex)[0]);
            }
            if (tab.content.loaded === false) {
                tab.content.element.innerHTML = this._options.loading.contentLoading;
                this.handleShowTab(tab);
                this.handleLoadContent(tab);

            }
            else {
                this.handleShowTab(tab);
            }
        }
    }

    private handleShowTab(tab: TabNavigationElement): void {
        addClass(tab.navigation, this._options.classes.active);
        addClass(tab.content.element, this._options.classes.visible);
        this._visibleTabIndex = tab.index;
        this.eventNamespaceFn(tab.content.element, 'shown');
    }

    handleLoadContent(tab: TabNavigationElement): void {
        fetch(tab.target, {
            method: 'GET'
        })
            .then(response => {
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('text/html')) {
                    return response.text();
                }
            })
            .then(data => {
                if (data != null && data.length > 0) {
                    tab.content.element.innerHTML = data;
                    tab.content.loaded = true;
                    this.eventNamespaceFn(tab.content.element, 'loaded');

                }
            });
    }

    private handleHideTab(tab: TabNavigationElement): void {
        removeClass(tab.navigation, this._options.classes.active);
        removeClass(tab.content.element, this._options.classes.visible);
        this.eventNamespaceFn(tab.content.element, 'hidden');
    }
}

export function tabs(
    wrapper: HTMLElement,
    options?: TabsOptions
): Tabs {
    return new Tabs(wrapper, options);
}
